import { z } from "zod"

/** The largest value the baseline's INTEGER columns can hold. */
export const ALFRED_IMPACT_BASELINE_MAX = 2147483647

// At least 1 kr, as the differences are also shown in percent of it.
const kronor = z.number().int().min(1).max(ALFRED_IMPACT_BASELINE_MAX)

/**
 * The store's average service order in one calendar month before Alfred. A
 * store that only knows its labor leaves avgOrderValue empty, and then only
 * labor is compared.
 */
export const AlfredImpactBaselineMonthSchema = z.object({
  avgOrderValue: kronor.nullable(),
  avgLaborValue: kronor.nullable(),
})

export type AlfredImpactBaselineMonth = z.infer<
  typeof AlfredImpactBaselineMonthSchema
>

/**
 * One entry per calendar month, January first: the index is the Month. Each
 * month since Alfred is compared with the same calendar month before.
 */
export const AlfredImpactBaselineSchema = z
  .object({
    months: z.array(AlfredImpactBaselineMonthSchema).length(12),
  })
  .refine(
    (baseline) =>
      baseline.months.some(
        (month) => month.avgOrderValue !== null || month.avgLaborValue !== null,
      ),
    { message: "Enter an average order or labor value for at least one month" },
  )

export type AlfredImpactBaseline = z.infer<typeof AlfredImpactBaselineSchema>

/**
 * What the months are compared on: order value when the store knows it for
 * any month, else labor.
 */
export type AlfredImpactMetric = "orderValue" | "laborValue"

/** How an average compares with the store's baseline. */
export interface AlfredImpactDifference {
  /** Kronor per job above (or below, when negative) the baseline. */
  perJob: number
  /** The same difference in percent of the baseline. */
  percent: number
}

export interface AlfredImpactPeriod {
  /** Completed jobs with an order value. */
  jobCount: number
  /** Null when there were no jobs. */
  avgOrderValue: number | null
  avgLaborValue: number | null
  /** The average before Alfred, or null when the store didn't enter one. */
  orderValueBaseline: number | null
  laborValueBaseline: number | null
  /** Null without jobs or without a baseline for that value. */
  orderValueDifference: AlfredImpactDifference | null
  laborValueDifference: AlfredImpactDifference | null
  /** The compared metric's numbers, picked from the above. */
  comparedAverage: number | null
  comparedBaseline: number | null
  comparedDifference: AlfredImpactDifference | null
  /**
   * The compared metric's difference per job times the jobs: what Alfred
   * brought in on top of the baseline. Null without jobs or a baseline.
   */
  extraRevenue: number | null
}

export interface AlfredImpactMonth extends AlfredImpactPeriod {
  /** The calendar month, as "YYYY-MM". */
  month: string
  /** The month isn't over yet, so its numbers will still change. */
  isCurrentMonth: boolean
}

/**
 * All months together. The averages and baselines only count the months
 * that have a baseline for that value, so they compare like for like; the
 * baselines are weighted by each month's jobs.
 */
export interface AlfredImpactSummary extends AlfredImpactPeriod {
  /** Compared months whose average was above the baseline. */
  monthsAboveBaseline: number
  /** Months with jobs and a baseline to compare them with. */
  comparedMonths: number
}

export interface AlfredImpactResponse {
  /** Every month empty until the store has entered its numbers. */
  baseline: AlfredImpactBaseline
  /** Null without a baseline. */
  comparedMetric: AlfredImpactMetric | null
  /** Oldest first, from the first month with jobs to the current month. */
  months: AlfredImpactMonth[]
  summary: AlfredImpactSummary
}
